import dedent from 'dedent';
import {z} from 'zod';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {defineToolRegistryManifest} from '../../types/base-tool-types';

const toolName = 'delete-remote-files' as const;

const argumentZod = z.object({
  paths: z
    .array(
      z
        .string()
        .describe(
          'The path of the file to delete, relative to the project root (e.g. "types.ts", "utils/format.ts")'
        )
    )
    .min(1)
    .describe('All files to delete in this single call.'),
});

export const deleteRemoteFilesTool = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Delete files',
  prettyStatus: {
    executing: 'Deleting files',
    executed: 'Deleted files',
    failed: 'Failed to delete files',
    partial: 'Deleted files with errors',
  },
  llmTool: {
    name: toolName,
    description: dedent`
      Deletes files from the code component sandbox environment. Each path is relative to the project root. Pass every file to remove as a single \`paths\` array in ONE call.

      Use this when a file is genuinely no longer needed — for example after consolidating two modules, or when a refactor leaves a helper unused. Deleting a file also removes it from the component's saved source, so it will not come back on reload.

      Do NOT delete the main \`{componentId}.webflow.tsx\` entry file: every component needs its entry point, and removing it breaks the component.

      Prefer editing a file over deleting and recreating it — a delete followed by a write loses the file's history and is slower than an edit.
    `,
    inputSchema: argumentZod,
  },
  type: 'designer',
  permission: [{resource: 'SiteSymbol', action: 'modify'}],
  confirmBeforeExecuting: false,
  // Multi-file components are the only case where a delete is meaningful, so
  // this rides the same gate. Without it the agent could delete files on sites
  // that have no multi-file UI to see or undo the result.
  featureGate: FEATURE_FLAGS.VCC_MULTIFILE,
  // Deletes are N filesystem removals with no install or type-check, so they
  // are far cheaper than the write path. Matches write-remote-file's 15s
  // rather than the batch-write 60s.
  timeout: 15000,
});
